import React, { lazy, Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import ModuleBarHeader from './ModuleBarHeader';
import ModuleBarStack from './ModuleBarStack';
import ModuleBarThemer from './ModuleBarThemer';
import MoreButton from './MoreButton';
import { injectModuleScript, loadModule } from 'utils/ModuleUtils';
import { isMobile, LauncherSize, ModuleBarSkins } from '@conversion-cloud-widget/UI';

const ModuleBarFrame = styled.div`
    ${props => props.adjustments}
    position: relative;

    &.engaged {
        bottom: ${props => (props.skin === ModuleBarSkins.WALLET && props.yPosition !== 'top' ? '60px' : '0px')};
        #clx-container__module-bar {
            border: none;
            height: 0;

            &.top {
                height: auto;
            }

            .clx-module__launcher {
                display: none;
            }
        }

        #clx-container__module-bar.small {
            overflow: visible;
            background-color: transparent;
            position: static;

            &.skin1 {
                padding: 0px;
            }
        }

        #clx-container__module-bar {
            -webkit-animation: unset !important;
            -webkit-transition: none !important;
            animation: unset !important;
            transition: none !important;

            &.bottom {
                margin-top: 0;
            }
        }
    }
`;

const ModuleBar = ({
    adjustments,
    primaryColor,
    companyId,
    companyPhone,
    mode,
    modules,
    moduleCount,
    size,
    skin,
    textColor,
    walletGreeting,
    xPosition,
    yPosition,
    widgetExpanded,
    animation,
    collapseThreshhold
}) => {
    const [colors, setColors] = useState({ primary: primaryColor, text: textColor || '#FFFFFF' });

    function updateColors(newColors) {
        newColors.text = newColors.text || '#FFFFFF';
        const moduleBar = document.querySelector('#clx-container__module-bar');
        moduleBar.removeAttribute('style');
        setColors({ ...colors, ...newColors });
    }

    const containsConcessionManager = modules.some(m => m.name === 'MODULE_CONCESSIONMANAGER');
    const borderRadius = '4px';

    const formatPhoneNumber = phone => {
        const match = phone.replace(/\D/g, '').match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        return match ? `(${match[2]}) ${match[3]}-${match[4]}` : '';
    };

    const currentCollapseThreshold = isMobile() ? collapseThreshhold.mobile : collapseThreshhold.desktop;

    let className = `${size} ${xPosition} ${yPosition} ${skin}`;
    if (isMobile()) {
        className += ` mobile`;
    }

    /* 
        Logic to determine how to initially render the widget
        If there are fewer than 4 - render in a row
        If there are exactly 4 - render in a grid
        If there are more than 4 - collapse the widget to show one module and a button to expand/collapse
    */
    if (moduleCount === currentCollapseThreshold) {
        className += ' grid';
    }

    if (moduleCount > currentCollapseThreshold) {
        className += ' multiple';
    }

    if (animation !== '') {
        className = `${className} animation`;
    }

    if (!!widgetExpanded && !isMobile() && moduleCount > currentCollapseThreshold) {
        className += ' opened';
    }

    useEffect(() => {
        updateColors({ primary: primaryColor, text: textColor });
    }, [primaryColor, textColor, mode]);

    return (
        <ModuleBarFrame
            id="clx-container__module-bar-frame"
            adjustments={adjustments}
            skin={skin || ''}
            yPosition={yPosition}
        >
            <ModuleBarThemer
                animation={animation}
                backgroundColor={mode === 'dark' ? colors.primary : colors.text}
                borderRadius={borderRadius}
                className={className}
                containsConcessionManager={containsConcessionManager}
                height={LauncherSize[size]?.height}
                mode={mode}
                moduleCount={moduleCount}
                skin={skin || ''}
                textColor={colors.text}
                yPosition={yPosition}
                size={size}
                widgetExpanded={widgetExpanded}
            >
                {skin === ModuleBarSkins.WALLET && (
                    <ModuleBarHeader
                        backgroundColor={colors.primary}
                        subheading={walletGreeting.subheading}
                        heading={walletGreeting.heading}
                        textColor={colors.text}
                    />
                )}
                <div className="clx-container__module-bar__wrapper">
                    {modules.map((module, index) => {
                        if (!module.url) {
                            console.log('Could not find module URL.', {
                                module: module.name,
                                companyId,
                                site: document.location
                            });
                            return;
                        }

                        // Inject the remote module script into the DOM
                        const { ready, failed } = injectModuleScript(module.name, module.url);

                        if (ready) {
                            const Component = lazy(loadModule(module.name, './Module'));

                            return (
                                <Suspense fallback="" key={index}>
                                    <Component
                                        backgroundColor={colors.primary}
                                        order={index + 1}
                                        skin={skin}
                                        textColor={colors.text}
                                        modules={modules}
                                    />
                                </Suspense>
                            );
                        }

                        if (failed) {
                            console.log('Failed to load module script.', {
                                module: module.name,
                                companyId,
                                site: document.location
                            });
                        }
                    })}
                </div>
                {skin === ModuleBarSkins.WALLET && (
                    <p className="clx-container__module-bar__footer">
                        Need to reach us directly?&nbsp;
                        {isMobile() ? (
                            <a href={`tel:${companyPhone}`}>Call Us!</a>
                        ) : (
                            <span>Call us: {formatPhoneNumber(companyPhone)}</span>
                        )}
                    </p>
                )}
                {((skin === ModuleBarSkins.DEFAULT && moduleCount > currentCollapseThreshold) ||
                    (skin === ModuleBarSkins.WALLET && moduleCount > 1)) && (
                        <MoreButton
                            fill={mode === 'light' ? colors.primary : colors.text}
                            height={skin === ModuleBarSkins.WALLET ? 64 : LauncherSize[size]?.height}
                            yPosition={yPosition}
                            skin={skin}
                        />
                    )}
            </ModuleBarThemer>
            {skin === ModuleBarSkins.WALLET && modules.length > 1 && (
                <ModuleBarStack xPosition={xPosition} yPosition={yPosition} />
            )}
        </ModuleBarFrame>
    );
};

export default ModuleBar;
